import axios from "axios";
import { t } from "i18next";

import {
  DEFAULT_GOOGLE_MAP_TILE,
  GOOGLE_ATTRIBUTION_FALLBACK,
  GOOGLE_TILE_CREATE_SESSION_URL,
} from "../constants";
import { googleApiKey, Log } from "../helpers";
import {
  ICreateTileSessionArguments,
  IGetViewportAttributionArguments,
  IGoogleSessionToken,
} from "../models";

export class GoogleApi {
  static async createTileSession({
    language,
    region,
  }: ICreateTileSessionArguments): Promise<IGoogleSessionToken> {
    const googleKey = googleApiKey();

    if (!googleKey) {
      Log.warnAndToast(t("errors.noGoogleMapsKeyFoundInEnvironment"));
    }

    const requestUrl = `${GOOGLE_TILE_CREATE_SESSION_URL}?key=${googleKey || ""}`;
    const response = await axios<IGoogleSessionToken>({
      method: "POST",
      url: requestUrl,
      /** Google Tiles API is cross-origin; global axios defaults use `withCredentials` for our API cookies. */
      withCredentials: false,
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        mapType: DEFAULT_GOOGLE_MAP_TILE,
        language,
        region,
      },
    });

    return response.data;
  }

  /**
   * The copyright line Google requires for the tiles currently in view. It changes with the viewport
   * (different imagery providers per region/zoom), so it is re-fetched as the map moves.
   *
   * Engine-agnostic: both the Leaflet `AttributionUpdater` and the MapLibre `MapLibreGoogleAttribution`
   * call this. It lives here rather than beside either of them so the Leaflet removal cannot take it out.
   */
  static async getViewportAttribution({
    viewport,
    sessionToken,
    apiKey,
  }: IGetViewportAttributionArguments): Promise<string> {
    const url = `https://tile.googleapis.com/tile/v1/viewport?session=${sessionToken}&key=${apiKey}&zoom=${Math.round(viewport.zoom)}&north=${viewport.north}&south=${viewport.south}&east=${viewport.east}&west=${viewport.west}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      return data.copyright ?? GOOGLE_ATTRIBUTION_FALLBACK;
    } catch (error) {
      Log.error("Error fetching viewport attribution:", error);

      return GOOGLE_ATTRIBUTION_FALLBACK;
    }
  }
}
